import type { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'

/**
 * The display state of one month's Xero recognition sync.
 *
 * The backend (see `getJournalPushXeroSyncStatus`) returns raw counts, not a
 * status — we derive the state here. This layers a live `PUSHING` state on top of
 * the backend's four-state model (SYNCED / STALE / FAILED / NOT_SYNCED): when any
 * journal is mid-push (`posting > 0`) the month is actively syncing, which is more
 * useful to show than the state it is transitioning from.
 */
export type XeroMonthSyncState =
  | 'PUSHING'
  | 'FAILED'
  | 'STALE'
  | 'NOT_SYNCED'
  | 'SYNCED'

export type XeroSyncCounts = {
  pending: number
  posting: number
  posted: number
  failed: number
  lastSyncedAt: string | null
}

export type XeroMonthSync = XeroSyncCounts & {
  /** yyyy-MM — matches the journal-table period key. */
  monthKey: string
  state: XeroMonthSyncState
  /** pending + posting + posted + failed. */
  total: number
}

/**
 * Derive a month's sync state from its push counts. Precedence, highest first:
 * a push in flight, then a failure, then never-posted, then posted-but-changed.
 */
export const deriveMonthSyncState = (
  counts: XeroSyncCounts
): XeroMonthSyncState => {
  const isPushing = counts.posting > 0
  const hasFailure = counts.failed > 0
  const neverPosted = counts.lastSyncedAt === null
  const hasUnposted = counts.pending > 0

  if (isPushing) return 'PUSHING'
  if (hasFailure) return 'FAILED'
  if (neverPosted) return 'NOT_SYNCED'
  if (hasUnposted) return 'STALE'
  return 'SYNCED'
}

/** Map one API month row into the enriched view model used across the dashboard. */
export const toMonthSync = (
  month: Dashboardv99990101Api.GetJournalPushXeroSyncStatus.MonthSyncStatus
): XeroMonthSync => {
  const monthKey = `${month.year}-${String(month.month).padStart(2, '0')}`
  return {
    monthKey,
    pending: month.pending,
    posting: month.posting,
    posted: month.posted,
    failed: month.failed,
    lastSyncedAt: month.lastSyncedAt,
    total: month.pending + month.posting + month.posted + month.failed,
    state: deriveMonthSyncState(month)
  }
}

/**
 * Index the API months by their yyyy-MM key so a journal row can look up its
 * month's sync state in one step. `getPeriodMonthKey` derives the same key from a
 * journal's date, keeping the table cell and this map aligned.
 */
export const indexSyncByMonth = (
  months: ReadonlyArray<Dashboardv99990101Api.GetJournalPushXeroSyncStatus.MonthSyncStatus>
): Map<string, XeroMonthSync> =>
  months.reduce((acc, month) => {
    const sync = toMonthSync(month)
    return acc.set(sync.monthKey, sync)
  }, new Map<string, XeroMonthSync>())

/**
 * A month's sync from the index, or a NOT_SYNCED placeholder. A month with
 * journals but no push-state row has simply never been pushed to Xero.
 */
export const syncForMonth = (
  byMonthKey: Map<string, XeroMonthSync>,
  monthKey: string
): XeroMonthSync =>
  byMonthKey.get(monthKey) ?? {
    monthKey,
    state: 'NOT_SYNCED',
    pending: 0,
    posting: 0,
    posted: 0,
    failed: 0,
    total: 0,
    lastSyncedAt: null
  }
